/**
 * Helpers for the unauthenticated landing page (ported from the Electron
 * native splash screen).
 *
 * The landing page is shown only when the web app is loaded with the
 * `?splash=1` query param. The desktop app adds this param when it loads the
 * onboarding web app so the new hire first sees the audience picker instead of
 * being sent straight into the authenticated wizard. In a plain browser the
 * param is absent, so the app behaves exactly as it does today.
 */

import { STAGE } from "@/helpers/stageConfig";

/**
 * The query param the desktop app appends to request the landing page. Kept as
 * a constant so the detection and the wizard-entry logic can never drift.
 */
export const SPLASH_PARAM = "splash" as const;

/**
 * The value of {@link SPLASH_PARAM} that activates the landing page.
 */
export const SPLASH_PARAM_VALUE = "1" as const;

/**
 * Query param + value that route the authenticated wizard through IDPrism
 * rather than PassportV2. `useLoadRegion` reads this param and drops the PPv2
 * `login_hint` from the Cognito redirect when the value is `idprism`.
 */
export const AUTH_PARAM = "auth" as const;
export const IDPRISM_AUTH_VALUE = "idprism" as const;

/**
 * External destinations the landing cards open. These mirror the URLs the
 * Electron splash IPC handlers used, so behavior is unchanged when the splash
 * moves into the web app.
 */
export const LANDING_URLS = {
  /** First Aid new-hire portal — for contractors and temporary workers. */
  FIRST_AID: "https://firstaid.it.a2z.com/newhire",
  /** Mac laptop setup guide — for current or transferring employees. */
  LAPTOP_SETUP:
    "https://my.it.a2z.com/articles/setup/laptop-setup/set-up-mac-computer",
  /** First Aid chat entry point — used by the "Chat with IT" help action. */
  CHAT_WITH_IT: "https://firstaid.it.a2z.com/chatwithit",
} as const;

/**
 * Whether the current page load requested the landing page via `?splash=1`.
 *
 * @returns true when the landing page should be shown instead of the wizard.
 */
export const isLandingRequested = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  return (
    new URLSearchParams(window.location.search).get(SPLASH_PARAM) ===
    SPLASH_PARAM_VALUE
  );
};

/**
 * Leave the landing page and enter the authenticated wizard.
 *
 * Reloads the page with the `?splash=1` param stripped so the app mounts its
 * normal wizard flow (which triggers the Cognito login redirect). The rest of
 * the URL — path, other params, hash — is preserved, and the reloaded URL
 * stays param-free so a subsequent refresh does not bounce back to the landing
 * page.
 *
 * @param options.auth When set, also writes the `auth` query param (e.g.
 * `idprism`) so the wizard's Cognito redirect uses that identity provider.
 */
export const enterWizard = (options?: { auth?: string }): void => {
  const url = new URL(window.location.href);
  url.searchParams.delete(SPLASH_PARAM);
  if (options?.auth) {
    url.searchParams.set(AUTH_PARAM, options.auth);
  }
  window.location.assign(`${url.pathname}${url.search}${url.hash}`);
};

/**
 * Whether the app is running on a non-production stage (dev, beta, or gamma),
 * derived from the hostname via {@link STAGE}.
 */
export const isNonProd = (): boolean => STAGE !== "PROD";

/**
 * Open a URL in the external browser.
 *
 * In the desktop app the Electron window-open handler intercepts this and
 * routes it to the OS browser (same path as Chime/Zoom links). In a plain
 * browser it opens a new tab. `noopener,noreferrer` keeps the opened page from
 * accessing this window and strips the referrer.
 *
 * @param url The destination URL.
 */
export const openExternalLink = (url: string): void => {
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Whether the current client is Windows, per the browser user agent.
 *
 * Used to reproduce, on the web landing page, the Windows-only zoom the
 * Electron native splash applied. The native app ran
 * `webContents.setZoomLevel(-3)` on the splash view on Windows only (see
 * ViewsHandler); that scaled the splash to a ~0.58 zoom factor (Chromium's
 * zoom step is 1.2 per level, so 1.2^-3). macOS was left at 1.0, so the base
 * styles are sized for macOS and only Windows needs the scale-down.
 */
export const isWindows = (): boolean =>
  typeof navigator !== "undefined" && /windows/i.test(navigator.userAgent);
